import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { Modal, Dropdown } from 'react-bootstrap'
import Select from 'react-select'

import FilterSearch from 'pages/parts/FilterSearch'
import FilterPagination from 'pages/parts/FilterPagination'
import PeriodPicker from 'pages/parts/PeriodPicker'

import { GET, PUT, httpOk } from 'helpers/api'
import { formatDate, formatMoney, returnSign, findKeyFromArrayByValue } from 'helpers/helpers'
import { businessToday, businessMonthStart } from 'helpers/businessDay'

const STATUS_NEW = 1
const STATUS_HANDLED = 2

// Отзыв с такой оценкой и ниже — плохой: он же уходит в Telegram-группу (GuestFeedbackService)
const BAD_RATING_MAX = 3

function returnClassRating(rating) {
	if (!rating) return ''
	if (rating <= 2) return 'bg-danger text-white'
	if (rating <= BAD_RATING_MAX) return 'bg-warning text-white'
	return 'bg-success text-white'
}

function returnClassStatus(status) {
	return status === STATUS_HANDLED ? 'bg-success text-white' : 'bg-primary text-white'
}

// «Ответы анкеты · комментарий» одной строкой для ячейки таблицы
function reviewText(item) {
	const parts = (item.answers ?? []).map(answer => `${answer.question}: ${answer.answer}`)
	if (item.comment) parts.push(item.comment)
	return parts.join(' · ')
}

function daysAgo(days) {
	const date = new Date()
	date.setDate(date.getDate() - days)
	return formatDate(date, 'yyyy-MM-dd')
}

function Index() {
	const { t } = useTranslation()
	const [searchParams] = useSearchParams()

	// Переход из уведомления: ?pos_id=&highlight= — подсвечиваем отзыв и берём период пошире
	const highlightId = Number(searchParams.get('highlight')) || null
	const queryPosId = Number(searchParams.get('pos_id')) || null

	const sources = [
		{ id: 'cheque_qr', name: t('feedback_source_cheque_qr') },
		{ id: 'qr_menu', name: t('feedback_source_qr_menu') },
		{ id: 'telegram', name: t('feedback_source_telegram') },
		{ id: 'mobile_app', name: t('feedback_source_mobile_app') },
		{ id: 'website', name: t('feedback_source_website') },
	]

	// Вид отзыва (cheque_feedbacks.kind) — его пишет сервер при сохранении.
	// order_1/2/3 — отзывы к заказам зала, с собой и доставки
	const kinds = [
		{ id: 'order_1', name: t('feedback_kind_order_1') },
		{ id: 'order_2', name: t('feedback_kind_order_2') },
		{ id: 'order_3', name: t('feedback_kind_order_3') },
		{ id: 'order', name: t('feedback_kind_all_orders') },
		{ id: 'general', name: t('feedback_kind_general') },
		{ id: 'complaint', name: t('feedback_kind_complaint') },
	]

	function kindLabel(item) {
		if (!item.kind) return ''
		if (item.kind === 'order' && [1, 2, 3].includes(Number(item.order_type_id))) {
			return t(`feedback_kind_order_${Number(item.order_type_id)}`)
		}
		return t(`feedback_kind_${item.kind}`)
	}

	const ratings = [
		{ id: 'bad', name: t('feedback_bad_ratings') },
		{ id: 'good', name: t('feedback_good_ratings') },
		{ id: '1', name: '1' },
		{ id: '2', name: '2' },
		{ id: '3', name: '3' },
		{ id: '4', name: '4' },
		{ id: '5', name: '5' },
	]

	const statuses = [
		{ id: STATUS_NEW, name: t('feedback_status_new') },
		{ id: STATUS_HANDLED, name: t('feedback_status_handled') },
	]

	const defaultFilter = {
		start_date: highlightId ? daysAgo(90) : businessMonthStart(),
		end_date: businessToday(),
		pos_id: queryPosId,
		source: null,
		kind: null,
		rating: null,
		status: null,
		search: '',
	}

	const [poses, setPoses] = useState([])
	const [modal, setModal] = useState(false)
	const [selectedItem, setSelectedItem] = useState({})
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [filterData, setFilterData] = useState({ ...defaultFilter, random: 0 })
	const [pagination, setPagination] = useState({
		url: '/guest-feedbacks',
		response: null
	})
	const [data, setData] = useState({})

	const filterDataExist = Boolean(filterData.pos_id || filterData.source || filterData.kind || filterData.rating || filterData.status || filterData.search)

	async function searchWithFilter(nextFilterData = null) {
		var filterDataCopy = { ...(nextFilterData || filterData) }
		var urlParams = ""

		if (filterDataCopy.pos_id)
			urlParams += returnSign(urlParams) + 'pos_id=' + filterDataCopy.pos_id
		if (filterDataCopy.start_date)
			urlParams += returnSign(urlParams) + 'start_date=' + filterDataCopy.start_date
		if (filterDataCopy.end_date)
			urlParams += returnSign(urlParams) + 'end_date=' + filterDataCopy.end_date
		if (filterDataCopy.source)
			urlParams += returnSign(urlParams) + 'source=' + filterDataCopy.source
		if (filterDataCopy.kind)
			urlParams += returnSign(urlParams) + 'kind=' + filterDataCopy.kind
		if (filterDataCopy.rating)
			urlParams += returnSign(urlParams) + 'rating=' + filterDataCopy.rating
		if (filterDataCopy.status)
			urlParams += returnSign(urlParams) + 'status=' + filterDataCopy.status
		if (filterDataCopy.search)
			urlParams += returnSign(urlParams) + 'search=' + encodeURIComponent(filterDataCopy.search)
		if (filterDataCopy.page)
			urlParams += returnSign(urlParams) + 'page=' + filterDataCopy.page

		const response = await GET('/guest-feedbacks' + urlParams, {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data)
			setPagination({ ...pagination, 'response': response.data })
		}

		setFilterData(filterDataCopy)
		setDropdownIsOpen(false)
	}

	function handleSearchChange(search) {
		const nextFilterData = { ...filterData, search, page: null }
		setFilterData(nextFilterData)
		searchWithFilter(nextFilterData)
	}

	function resetFilter() {
		setFilterData({ ...defaultFilter, pos_id: null, random: filterData.random + 1 })
	}

	async function paginate(page) {
		const nextFilterData = { ...filterData, 'page': page }
		setFilterData(nextFilterData)
		searchWithFilter(nextFilterData)
	}

	function toggleModal(bool, item = {}) {
		setSelectedItem(item)
		setModal(bool)
	}

	async function changeStatus(status) {
		const response = await PUT(`/guest-feedbacks/${selectedItem.id}/status`, {
			status,
			handle_note: selectedItem.handle_note ?? '',
		}, { loader: true })

		if (httpOk(response)) {
			toggleModal(false)
			searchWithFilter()
		}
	}

	async function getPoses() {
		var response = await GET('/helper/poses')
		if (httpOk(response)) setPoses(response.data)
	}

	useEffect(() => {
		getPoses()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		searchWithFilter()
	}, [filterData.random]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex mb-2">
				<h4 className="vertical-center">
					{t('guest_feedbacks')} [{data?.total ?? 0}]
				</h4>
			</div>

			<div className="row mb-2">
				<div className="col-md-4">
					<div className="card mb-2">
						<div className="card-body">
							<div className="text-muted">{t('feedback_avg_rating')}</div>
							<h4 className="mb-0">{data?.summary?.avg_rating ?? '—'}</h4>
						</div>
					</div>
				</div>
				<div className="col-md-4">
					<div className="card mb-2 cursor" onClick={() => searchWithFilter({ ...filterData, rating: 'bad', page: null })}>
						<div className="card-body">
							<div className="text-muted">{t('feedback_bad_count')}</div>
							<h4 className="mb-0 text-danger">{data?.summary?.bad ?? 0}</h4>
						</div>
					</div>
				</div>
				<div className="col-md-4">
					<div className="card mb-2 cursor" onClick={() => searchWithFilter({ ...filterData, status: STATUS_NEW, page: null })}>
						<div className="card-body">
							<div className="text-muted">{t('feedback_new_count')}</div>
							<h4 className="mb-0 text-primary">{data?.summary?.new ?? 0}</h4>
						</div>
					</div>
				</div>
			</div>

			<div className="card">
				<div className="card-body">

					<div className="d-flex justify-content-between mb-3">
						<div className="d-flex gap-2">
							<div>
								<FilterSearch
									value={filterData.search}
									onSearchChange={handleSearchChange}>
								</FilterSearch>
							</div>
							<div>
								<Dropdown onToggle={() => setDropdownIsOpen(!dropdownIsOpen)} show={dropdownIsOpen}>
									<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
										{t('filter')}
									</Dropdown.Toggle>

									<Dropdown.Menu className="dropdown-filter">
										<div className="row">
											<div className="form-group col-md-6">
												<label>{t('pos')}</label>
												<Select
													isClearable
													options={poses}
													value={poses.find(option => option.id === filterData.pos_id) || ''}
													onChange={(option) => setFilterData({ ...filterData, 'pos_id': option ? option.id : null })}
													placeholder={t('all')}
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
											<div className="form-group col-md-6">
												<label>{t('source')}</label>
												<Select
													isClearable
													options={sources}
													value={sources.find(option => option.id === filterData.source) || ''}
													onChange={(option) => setFilterData({ ...filterData, 'source': option ? option.id : null })}
													placeholder={t('all')}
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
											<div className="form-group col-md-6">
												<label>{t('feedback_kind')}</label>
												<Select
													isClearable
													options={kinds}
													value={kinds.find(option => option.id === filterData.kind) || ''}
													onChange={(option) => setFilterData({ ...filterData, 'kind': option ? option.id : null })}
													placeholder={t('all')}
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
										</div>
										<div className="row">
											<div className="form-group col-md-6">
												<label>{t('rating')}</label>
												<Select
													isClearable
													options={ratings}
													value={ratings.find(option => option.id === filterData.rating) || ''}
													onChange={(option) => setFilterData({ ...filterData, 'rating': option ? option.id : null })}
													placeholder={t('all')}
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
											<div className="form-group col-md-6">
												<label>{t('status')}</label>
												<Select
													isClearable
													options={statuses}
													value={statuses.find(option => option.id === filterData.status) || ''}
													onChange={(option) => setFilterData({ ...filterData, 'status': option ? option.id : null })}
													placeholder={t('all')}
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
										</div>
										<div className="row">
											<div className="form-group col-md-12">
												<label>{t('period')}</label>
												<PeriodPicker
													withTime={false}
													startDate={filterData.start_date}
													endDate={filterData.end_date}
													minDate={new Date(new Date().getFullYear() - 1, 0, 1)}
													maxDate={new Date(new Date().getFullYear(), 11, 31)}
													onChange={(period) => setFilterData({ ...filterData, ...period })}
												/>
											</div>
										</div>
										<div className="row mt-3">
											<div className="col-md-12">
												<div className="d-flex justify-content-end gap-2">
													<button className="btn btn-secondary" onClick={() => setDropdownIsOpen(false)}>{t('cancel')}</button>
													<button className="btn btn-primary" onClick={() => searchWithFilter({ ...filterData, page: null })}>{t('apply')}</button>
												</div>
											</div>
										</div>
									</Dropdown.Menu>
								</Dropdown>
							</div>
						</div>
					</div>

					{filterDataExist &&
						<div className="filter-block">
							<div className="filter">
								<strong>{t('filter')}</strong>
								<br />
								<span className="me-2">
									<b>{t('period')}: </b>
									{formatDate(filterData.start_date)} | {formatDate(filterData.end_date)}
								</span>
								{filterData.pos_id &&
									<span className="me-2">
										<b>{t('pos')}: </b>
										{findKeyFromArrayByValue(poses, 'id', filterData.pos_id, 'name')}
									</span>
								}
								{filterData.source &&
									<span className="me-2">
										<b>{t('source')}: </b>
										{findKeyFromArrayByValue(sources, 'id', filterData.source, 'name')}
									</span>
								}
								{filterData.kind &&
									<span className="me-2">
										<b>{t('feedback_kind')}: </b>
										{findKeyFromArrayByValue(kinds, 'id', filterData.kind, 'name')}
									</span>
								}
								{filterData.rating &&
									<span className="me-2">
										<b>{t('rating')}: </b>
										{findKeyFromArrayByValue(ratings, 'id', filterData.rating, 'name')}
									</span>
								}
								{filterData.status &&
									<span className="me-2">
										<b>{t('status')}: </b>
										{findKeyFromArrayByValue(statuses, 'id', filterData.status, 'name')}
									</span>
								}
								{filterData.search &&
									<span className="me-2">
										<b>{t('search')}: </b>
										<span>{filterData.search}</span>
									</span>
								}
							</div>
							<div className="vertical-center">
								<button className="btn btn-outline-dark text-uppercase"
									onClick={() => resetFilter()}>
									{t('reset')}
								</button>
							</div>
						</div>
					}

					<div className="table-responsive">
						<table className="table table-bordered table-hover mb-0">
							<thead>
								<tr>
									<th style={{ 'width': '10%' }} className="text-center">{t('date')}</th>
									<th style={{ 'width': '12%' }}>{t('pos')}</th>
									<th style={{ 'width': '12%' }}>{t('feedback_kind')}</th>
									<th style={{ 'width': '10%' }} className="text-center">{t('source')}</th>
									<th style={{ 'width': '8%' }} className="text-center">{t('table')}</th>
									<th style={{ 'width': '8%' }} className="text-center">{t('cheque')}</th>
									<th style={{ 'width': '12%' }}>{t('client')}</th>
									<th style={{ 'width': '6%' }} className="text-center">{t('rating')}</th>
									<th style={{ 'width': '20%' }}>{t('feedback_review')}</th>
									<th style={{ 'width': '8%' }} className="text-center">{t('status')}</th>
								</tr>
							</thead>
							<tbody>
								{data?.data?.map((item) => (
									<tr key={item.id}
										className={`cursor ${highlightId === item.id ? 'table-tr-active' : ''}`}
										onClick={() => toggleModal(true, item)}>
										<td className="text-center text-nowrap">{formatDate(item.created_at, 'dd.MM.yy HH:mm')}</td>
										<td>{item.pos_name}</td>
										<td className="td-ellipsis" title={kindLabel(item)}>{kindLabel(item)}</td>
										<td className="text-center">{t(`feedback_source_${item.source}`)}</td>
										<td className="text-center">{item.table_name ?? ''}</td>
										<td className="text-center">{item.cheque_number ?? ''}</td>
										<td>
											{item.client_name ?? ''}
											{(item.client_phone || item.guest_phone) &&
												<div className="text-muted">{item.client_phone ? '+' + item.client_phone : item.guest_phone}</div>
											}
										</td>
										<td className={`text-center ${returnClassRating(item.rating)}`}>{item.rating ?? ''}</td>
										<td className="td-ellipsis" title={reviewText(item)}>{reviewText(item)}</td>
										<td className={`text-center ${returnClassStatus(item.status)}`} title={item.handle_note ?? ''}>
											{item.status === STATUS_HANDLED ? t('feedback_status_handled') : t('feedback_status_new')}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{pagination.response &&
						<FilterPagination
							pagination={pagination}
							paginate={paginate}>
						</FilterPagination>
					}
				</div>
			</div>

			{/* MODAL FEEDBACK */}
			<Modal show={modal} animation={false} centered scrollable size="lg" onHide={() => toggleModal(false)}>
				<Modal.Header closeButton>
					<Modal.Title>
						{t('feedback_review')} · {formatDate(selectedItem.created_at, 'dd.MM.yy HH:mm')}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ height: '500px' }}>
					<table className="table table-sm mb-3">
						<tbody>
							<tr>
								<td className="text-muted" style={{ width: '35%' }}>{t('pos')}</td>
								<td>{selectedItem.pos_name}</td>
							</tr>
							<tr>
								<td className="text-muted">{t('feedback_kind')}</td>
								<td>{kindLabel(selectedItem)}</td>
							</tr>
							<tr>
								<td className="text-muted">{t('source')}</td>
								<td>{selectedItem.source ? t(`feedback_source_${selectedItem.source}`) : ''}</td>
							</tr>
							{selectedItem.table_name &&
								<tr>
									<td className="text-muted">{t('table')}</td>
									<td>{[selectedItem.hall_name, selectedItem.table_name].filter(Boolean).join(', ')}</td>
								</tr>
							}
							{selectedItem.cheque_number &&
								<tr>
									<td className="text-muted">{t('cheque')}</td>
									<td>
										№{selectedItem.cheque_number}
										{selectedItem.total_amount_service !== null && selectedItem.total_amount_service !== undefined &&
											<span className="text-muted"> · {formatMoney(selectedItem.total_amount_service)}</span>
										}
									</td>
								</tr>
							}
							{selectedItem.waiter_name &&
								<tr>
									<td className="text-muted">{t('waiter')}</td>
									<td>{selectedItem.waiter_name}</td>
								</tr>
							}
							{(selectedItem.client_name || selectedItem.client_phone) &&
								<tr>
									<td className="text-muted">{t('client')}</td>
									<td>{[selectedItem.client_name, selectedItem.client_phone ? '+' + selectedItem.client_phone : null].filter(Boolean).join(' · ')}</td>
								</tr>
							}
							{selectedItem.guest_phone &&
								<tr>
									<td className="text-muted">{t('feedback_guest_phone')}</td>
									<td>{selectedItem.guest_phone}</td>
								</tr>
							}
							<tr>
								<td className="text-muted">{t('rating')}</td>
								<td>{selectedItem.rating ? `${'★'.repeat(selectedItem.rating)} ${selectedItem.rating}/5` : '—'}</td>
							</tr>
							{(selectedItem.answers ?? []).map((answer, index) => (
								<tr key={index}>
									<td className="text-muted">{answer.question}</td>
									<td style={{ whiteSpace: 'pre-wrap' }}>{answer.answer}</td>
								</tr>
							))}
							{selectedItem.comment &&
								<tr>
									<td className="text-muted">{t('comment')}</td>
									<td style={{ whiteSpace: 'pre-wrap' }}>{selectedItem.comment}</td>
								</tr>
							}
							{selectedItem.status === STATUS_HANDLED && selectedItem.handled_by_name &&
								<tr>
									<td className="text-muted">{t('feedback_handled_by')}</td>
									<td>{selectedItem.handled_by_name} · {formatDate(selectedItem.handled_at, 'dd.MM.yy HH:mm')}</td>
								</tr>
							}
						</tbody>
					</table>

					<div className="form-group">
						<label>{t('feedback_handle_note')}</label>
						<textarea className="form-control"
							style={{ 'resize': 'none' }}
							rows="3"
							maxLength={1000}
							value={selectedItem.handle_note ?? ''}
							onChange={(e) => setSelectedItem({ ...selectedItem, 'handle_note': e.target.value })} />
					</div>
				</Modal.Body>
				<Modal.Footer>
					<div className="d-flex w-100 gap-2">
						<button type="button" className="btn btn-outline-warning w-100"
							onClick={() => toggleModal(false)}>
							{t('cancel')}
						</button>
						{selectedItem.status === STATUS_HANDLED ?
							<button type="button" className="btn btn-outline-primary w-100"
								onClick={() => changeStatus(STATUS_NEW)}>
								{t('feedback_mark_new')}
							</button>
							:
							<button type="button" className="btn btn-primary w-100"
								onClick={() => changeStatus(STATUS_HANDLED)}>
								{t('feedback_mark_handled')}
							</button>
						}
					</div>
				</Modal.Footer>
			</Modal>
			{/* MODAL FEEDBACK */}
		</>
	)
}

export default Index
